import { createSelector } from 'reselect'
import type { Group } from 'types/group'
import type { JsonNotification, NotificationType } from 'types/notification'
import type { State } from 'types/redux'
import { getUserInboxes } from '@disco/state-management/selectors/groups'
import {
  getUser,
  getUserCounterTimestamp,
} from '@disco/state-management/selectors/user'
import { getUnreadMessageCounter } from '@disco/utils/notifications'
/**
 * Gets the unread notifications from the state.
 * @param state - The Redux state
 * @returns Array of unread notification objects
 */
export const getUnreadNotifications = (state: State): NotificationType[] =>
  state.notifications.unreadNotifications
/**
 * Gets the count of unread notifications after the user's counter timestamp.
 * @param state - The Redux state
 * @returns Number of unread notifications since the counter timestamp
 */
export const getUnreadNotificationsCount = createSelector(
  [getUserCounterTimestamp, getUnreadNotifications],
  (counterTimestamp, notifications) => {
    const date = counterTimestamp ? new Date(counterTimestamp) : new Date()
    return notifications.filter(
      n =>
        typeof n !== 'string' &&
        new Date((n as JsonNotification).timestamp) > date,
    ).length
  },
)
/**
 * Gets the unread notifications counter for user inboxes.
 * @param state - The Redux state
 * @returns Object with counters by inbox ID and total count
 */
export const getUserInboxesNotificationsCounter = createSelector(
  [getUser, getUserInboxes, getUnreadNotifications],
  (user, userInboxes, unreadNotifications) => {
    let result = {
      byId: {},
      total: 0,
    }
    userInboxes.forEach((group: Group) => {
      const counter = getUnreadMessageCounter(
        user,
        unreadNotifications,
        group.id,
      )

      if (counter) {
        result = {
          byId: { ...result.byId, [group.id]: counter },
          total: result.total + counter,
        }
      }
    })
    return result
  },
)
